import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getAnalyticsSummary } from "@/lib/analytics-summary.functions";
import { supabase } from "@/integrations/supabase/client";
import { toCsv, downloadCsv } from "@/lib/csv-export";


type Summary = {
  days: number;
  total: number;
  page_views: number;
  by_event: Record<string, number>;
  by_lang: Record<string, number>;
  cta_clicks: Record<string, number>;
  doc_downloads: Record<string, number>;
  faq_opens: Record<string, number>;
  top_referrers: Record<string, number>;
  top_pages: Record<string, number>;
  page_views_daily: { day: string; count: number }[];
  daily: { day: string; count: number }[];
};


export const Route = createFileRoute("/admin/analytics")({
  component: AnalyticsDashboard,
  head: () => ({
    meta: [
      { title: "Analytics — SFN (internal)" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function AnalyticsDashboard() {
  const [days, setDays] = useState(30);
  // Filter controls (applied on submit so typing doesn't hammer the backend).
  const [pathInput, setPathInput] = useState("");
  const [refInput, setRefInput] = useState("");
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [filters, setFilters] = useState({ path: "", ref: "", startDate: "", endDate: "" });

  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const fetchSummary = useServerFn(getAnalyticsSummary);

  // Session gate: calling the auth-protected server function without a session
  // throws "Unauthorized: No authorization header provided". Check first.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: s }) => {
      if (!cancelled) setSignedIn(Boolean(s.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(Boolean(session));
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (signedIn !== true) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSummary({ data: { days, ...filters } })
      .then((result) => {
        if (cancelled) return;
        setData(result as unknown as Summary);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load analytics — your session may have expired.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, filters, fetchSummary, signedIn]);

  const applyFilters = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFilters({
        path: pathInput.trim(),
        ref: refInput.trim(),
        startDate: startInput,
        endDate: endInput,
      });
    },
    [pathInput, refInput, startInput, endInput],
  );

  const resetFilters = useCallback(() => {
    setPathInput("");
    setRefInput("");
    setStartInput("");
    setEndInput("");
    setFilters({ path: "", ref: "", startDate: "", endDate: "" });
  }, []);

  const dateRangeActive = Boolean(filters.startDate || filters.endDate);

  const exportCsv = useCallback(() => {
    if (!data) return;
    const rows: (string | number | null)[][] = [];
    rows.push(["SFN analytics export"]);
    rows.push(["generated_at", new Date().toISOString()]);
    rows.push([
      "range",
      dateRangeActive
        ? `${filters.startDate || "…"} → ${filters.endDate || "…"}`
        : `last ${days} days`,
    ]);
    rows.push(["path_filter", filters.path || "(none)"]);
    rows.push(["referrer_filter", filters.ref || "(none)"]);
    rows.push([]);
    rows.push(["section", "key", "value"]);
    rows.push(["summary", "page_views", data.page_views ?? 0]);
    rows.push(["summary", "total_events", data.total ?? 0]);
    (data.page_views_daily ?? []).forEach((d) =>
      rows.push(["page_views_daily", d.day, d.count]),
    );
    (data.daily ?? []).forEach((d) => rows.push(["events_daily", d.day, d.count]));
    Object.entries(data.top_pages ?? {})
      .sort((a, b) => b[1] - a[1])
      .forEach(([k, v]) => rows.push(["top_pages", k, v]));
    Object.entries(data.top_referrers ?? {})
      .sort((a, b) => b[1] - a[1])
      .forEach(([k, v]) => rows.push(["top_referrers", k, v]));
    Object.entries(data.by_event ?? {})
      .sort((a, b) => b[1] - a[1])
      .forEach(([k, v]) => rows.push(["by_event", k, v]));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`sfn-analytics-${stamp}.csv`, toCsv(rows));
  }, [data, days, filters, dateRangeActive]);

  const activeCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters],
  );

  const inputCls =
    "mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-saffron/50 focus:outline-none";
  const labelCls =
    "block text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-6 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-saffron">
              // Internal · Hidden route
            </p>
            <h1 className="mt-2 font-display text-4xl font-medium tracking-tight">
              Conversion Dashboard
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Privacy-friendly aggregates. No IP, no fingerprints, no PII — only event counts.
              Bots and refresh storms are filtered out.
            </p>
          </div>
          <div
            className={`inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1 ${
              dateRangeActive ? "opacity-40" : ""
            }`}
            title={dateRangeActive ? "Disabled while a custom date range is active" : undefined}
          >
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                disabled={dateRangeActive}
                onClick={() => setDays(d)}
                className={`rounded-full px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition ${
                  days === d && !dateRangeActive
                    ? "bg-gradient-to-br from-saffron to-coral text-obsidian"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {signedIn === true && (
          <form onSubmit={applyFilters} className="mt-8 rounded-2xl glass p-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <label className={labelCls}>
                From
                <input
                  type="date"
                  value={startInput}
                  max={endInput || undefined}
                  onChange={(e) => setStartInput(e.target.value)}
                  className={inputCls}
                />
              </label>
              <label className={labelCls}>
                To
                <input
                  type="date"
                  value={endInput}
                  min={startInput || undefined}
                  onChange={(e) => setEndInput(e.target.value)}
                  className={inputCls}
                />
              </label>
              <label className={labelCls}>
                Path contains
                <input
                  type="text"
                  value={pathInput}
                  placeholder="/villagepando"
                  onChange={(e) => setPathInput(e.target.value)}
                  className={inputCls}
                />
              </label>
              <label className={labelCls}>
                Referrer contains
                <input
                  type="text"
                  value={refInput}
                  placeholder="google.com"
                  onChange={(e) => setRefInput(e.target.value)}
                  className={inputCls}
                />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="rounded-full bg-gradient-to-br from-saffron to-coral px-5 py-2 text-xs font-semibold uppercase tracking-wider text-obsidian"
              >
                Apply filters
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-full border border-white/10 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground transition hover:text-foreground"
              >
                Reset
              </button>
              <button
                type="button"
                aria-label="Download analytics events as a CSV file"
                onClick={exportCsv}
                disabled={!data}
                className="rounded-full border border-saffron/40 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-saffron transition hover:bg-saffron/10 disabled:opacity-40"
              >
                Export CSV
              </button>
              {activeCount > 0 && (
                <span className="font-mono text-[11px] text-muted-foreground">
                  {activeCount} filter{activeCount > 1 ? "s" : ""} active
                </span>
              )}
            </div>
          </form>
        )}

        {signedIn === false && <AdminSignIn />}
        {signedIn === true && loading && (
          <p className="mt-10 text-sm text-muted-foreground">Loading…</p>
        )}
        {error && (
          <p className="mt-10 rounded-xl border border-coral/40 bg-coral/10 p-4 text-sm text-coral">
            {error}
          </p>
        )}

        {data && (
          <div className="mt-10 grid gap-6">
            <KpiRow data={data} />
            <DailyChart title="Page views per day" daily={data.page_views_daily ?? []} />
            <Panel title="Top referrers" rows={data.top_referrers} />
            <Panel title="Top pages" rows={data.top_pages} />
            <Panel title="Language switches" rows={data.by_lang} suffix="" />
            <Panel title="CTA clicks (hero + nav)" rows={data.cta_clicks} />
            <Panel title="Whitepaper downloads" rows={data.doc_downloads} />
            <Panel title="FAQ opens" rows={data.faq_opens} />
            <Panel title="All events" rows={data.by_event} />
            <DailyChart title="Daily events" daily={data.daily} />
          </div>
        )}
      </div>
    </div>
  );
}



function KpiRow({ data }: { data: Summary }) {
  const langTotal = sum(data.by_lang);
  const ctaTotal = sum(data.cta_clicks);
  const docTotal = sum(data.doc_downloads);
  const kpis = [
    { label: "Page views", value: data.page_views ?? 0 },
    { label: "Total events", value: data.total },
    { label: "CTA clicks", value: ctaTotal },
    { label: "Language switches", value: langTotal },
    { label: "PDF downloads", value: docTotal },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {kpis.map((k) => (
        <div key={k.label} className="rounded-2xl glass p-4">
          <div className="font-mono text-3xl font-bold">{k.value}</div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            {k.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function Panel({
  title,
  rows,
  suffix = "",
}: {
  title: string;
  rows: Record<string, number>;
  suffix?: string;
}) {
  const entries = Object.entries(rows ?? {}).sort((a, b) => b[1] - a[1]);
  const max = entries.length ? entries[0][1] : 0;
  return (
    <div className="rounded-2xl glass p-5">
      <h2 className="font-display text-lg font-medium tracking-tight">{title}</h2>
      {entries.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No data yet.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {entries.map(([k, v]) => (
            <li key={k} className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate font-mono text-xs text-foreground">{k}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {v}
                  {suffix}
                </span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                <div
                  className="h-full bg-gradient-to-r from-saffron to-coral"
                  style={{ width: `${max ? (v / max) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DailyChart({
  title = "Daily events",
  daily,
}: {
  title?: string;
  daily: { day: string; count: number }[];
}) {
  const max = daily.reduce((m, d) => Math.max(m, d.count), 0);
  return (
    <div className="rounded-2xl glass p-5">
      <h2 className="font-display text-lg font-medium tracking-tight">{title}</h2>
      {daily.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No data yet.</p>
      ) : (
        <div className="mt-4 flex h-32 items-end gap-1">
          {daily.map((d) => (
            <div
              key={d.day}
              title={`${d.day}: ${d.count}`}
              className="flex-1 rounded-t bg-gradient-to-t from-saffron/40 to-coral"
              style={{ height: `${max ? (d.count / max) * 100 : 0}%`, minHeight: 2 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function sum(o: Record<string, number>) {
  return Object.values(o ?? {}).reduce((a, b) => a + b, 0);
}

/** Minimal internal sign-in: the analytics summary is auth-gated server-side. */
function AdminSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setBusy(true);
      setMsg(null);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setBusy(false);
      if (error) setMsg(error.message);
    },
    [email, password],
  );

  return (
    <form onSubmit={submit} className="mt-10 max-w-sm rounded-2xl glass p-6">
      <h2 className="font-display text-lg font-medium tracking-tight">Sign in required</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Internal dashboard — analytics are only readable by an authenticated account.
      </p>
      <label className="mt-4 block text-xs uppercase tracking-wider text-muted-foreground">
        Email
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground"
        />
      </label>
      <label className="mt-3 block text-xs uppercase tracking-wider text-muted-foreground">
        Password
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground"
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        className="mt-4 w-full rounded-full bg-gradient-to-br from-saffron to-coral px-4 py-2.5 text-sm font-semibold text-obsidian disabled:opacity-60"
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
      {msg && <p className="mt-3 text-xs text-coral">{msg}</p>}
    </form>
  );
}
